import { useEffect, useId, useLayoutEffect, useRef, useState, type JSX, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';

// Rechnungsliste: Menü „···“ einer Zeile mit allem, was nicht der Knopf für den nächsten Schritt ist. Bedienung wie das
// Konto-Menü (billtoro/KontoMenue.tsx): Pfeiltasten, Pos1/Ende, Escape, Klick daneben und Tab hinaus schließen es.
// children: Einträge mit role="menuitem" (Knopf, Link, GesperrtKnopf) und Trennstriche (role="separator").
// Geht nach oben auf, wenn es unten nicht passt und oben schon; sonst nach unten, notfalls ins Bild gerollt (über dem
// Seitenanfang wäre es nicht mehr erreichbar). Immer nur ein Menü offen.
// Name des Knopfs: „Weitere Aktionen zu {nummer}“ oder label wörtlich (Kopf einer Seite, Karte); das Menü heißt wie der
// Knopf ohne „Weitere “. gross: Knopf 44 px im Aussehen der Knöpfe daneben (Seitenkopf, Karte, Leiste am Handy).

// Schließen des offenen Menüs (ein zweites schließt das erste)
let offen: (() => void) | null = null;

type Name = { nummer: string; label?: undefined } | { label: string; nummer?: undefined };

export function ZeilenMenue({ nummer, label, gross = false, children }: Name & { gross?: boolean; children: ReactNode }): JSX.Element {
  const name = label ?? `Weitere Aktionen zu ${nummer}`;
  const [open, setOpen] = useState(false);
  const [oben, setOben] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  const id = `re-menue-${useId()}`;

  function auf() {
    setOben(false);
    setOpen(true);
  }

  // Platz messen (oben nur, wenn es dort ganz passt), dann der erste Eintrag mit dem Fokus
  useLayoutEffect(() => {
    if (!open) return;
    const l = liste.current;
    const k = knopf.current;
    if (!l || !k) return;
    const r = k.getBoundingClientRect();
    const h = l.offsetHeight;
    const unten = innerHeight - r.bottom;
    if (unten < h + 12 && r.top >= h + 12) setOben(true);
    else if (l.getBoundingClientRect().bottom > innerHeight) l.scrollIntoView({ block: 'nearest' });
    l.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const zu = () => setOpen(false);
    offen?.();
    offen = zu;
    // Klick daneben oder Fokus landet woanders: zu (nicht schon beim Verlassen, siehe Konto-Menü)
    const close = (e: globalThis.MouseEvent | globalThis.FocusEvent | globalThis.KeyboardEvent) => {
      if (e instanceof globalThis.KeyboardEvent) {
        if (e.key !== 'Escape') return;
        setOpen(false);
        knopf.current?.focus();
      } else if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('focusin', close);
    document.addEventListener('keydown', close);
    return () => {
      if (offen === zu) offen = null;
      document.removeEventListener('mousedown', close);
      document.removeEventListener('focusin', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  function tasten(e: KeyboardEvent<HTMLDivElement>) {
    // Tab und Umschalt+Tab: zu, der Fokus geht vom Knopf aus weiter (auch, wenn danach auf der Seite nichts mehr kommt)
    if (e.key === 'Tab') {
      knopf.current?.focus();
      setOpen(false);
      return;
    }
    const items = [...(liste.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    const ziel =
      e.key === 'ArrowDown' ? (i + 1) % items.length
      : e.key === 'ArrowUp' ? (i - 1 + items.length) % items.length
      : e.key === 'Home' ? 0
      : e.key === 'End' ? items.length - 1
      : -1;
    if (ziel < 0) return;
    e.preventDefault();
    items[ziel]?.focus();
  }

  // Eintrag gewählt (nach seinem eigenen onClick): zu, Fokus zurück auf „···“. Ein Fenster, das der Eintrag öffnet,
  // gibt den Fokus beim Schließen dorthin zurück.
  function waehlen(e: MouseEvent<HTMLDivElement>) {
    const el = (e.target as Element).closest<HTMLElement>('[role="menuitem"]');
    if (!el || (el as HTMLButtonElement).disabled) return;
    setOpen(false);
    knopf.current?.focus();
  }

  return (
    <span className="re-mehr" ref={wrap}>
      <button
        ref={knopf}
        type="button"
        className={`re-mehr-knopf${gross ? ' gross' : ''}`}
        aria-label={name}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => (open ? setOpen(false) : auf())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            auf();
          }
        }}
      >
        <span aria-hidden="true">···</span>
      </button>
      {open && (
        <div ref={liste} id={id} role="menu" aria-label={name.replace(/^Weitere /, '')} className={`re-menue${oben ? ' oben' : ''}`} onKeyDown={tasten} onClick={waehlen}>
          {children}
        </div>
      )}
    </span>
  );
}
